import React from 'react';
import type { ComponentConfig, Fields } from '@puckeditor/core';
import { sanitizeUrl } from '@braintree/sanitize-url';
import styled from 'styled-components';
import { isHexColor } from '../fields/hex-color';

import { BORDER_COLOR_AI_SCHEMA, borderStyle, DEFAULT_BORDER } from '../fields/border';
import {
  BACKGROUND_IMAGE_FIELDS,
  BackgroundOverlay,
  DEFAULT_BACKGROUND_IMAGE_PROPS,
  backgroundOverlayStyle,
} from '../fields';
import type { BorderColor, BorderValue } from '../fields/border';
import {
  CONTAINER_TEXT_COLOR_AI_SCHEMA,
  containerColorStyle,
  inheritedTextColorFor,
  INHERIT_COLOR,
} from '../fields/container-colors';
import { PX_AI_SCHEMA } from '../fields/spacing-fields';
import { renderFieldGroups } from '../fields/field-groups';
import type { FieldGroupDefinition } from '../fields/field-groups';
import {
  BLOCK_SPACING,
  BRAND_COLOR,
  BRAND_CONTRAST_COLOR,
  BUTTON_FONT_WEIGHT,
  BUTTON_INSET,
  BUTTON_RADIUS,
  HEADING_LINE_HEIGHT,
  HEADING_PRESETS,
  IMAGE_ASPECT_RATIOS,
  IMAGE_FOCAL_POINTS,
  CONTAINER_WIDTHS,
  TEXT_COLOR_ROLES,
  lookup,
  resolveSurfaceColor,
} from '../fields/tokens';
import type {
  BlockSpacing,
  ContainerWidthOption,
  HeadingPreset,
  ImageAspectRatio,
  ImageFocalPoint,
} from '../fields/tokens';
import { altTextField, resolveAltText } from '../fields/widgets/alt-text-field';
import type { AltTextValue } from '../fields/widgets/alt-text-field';
import { BorderColorField } from '../fields/widgets/border-color-field';
import { ContainerTextColorField } from '../fields/widgets/container-text-color-field';
import { destinationField } from '../fields/widgets/destination-field';
import { focalPointField } from '../fields/widgets/focal-point-field';
import { AssetOrUrlImageSourceField } from '../fields/widgets/image-source-field';
import { NumberField } from '../fields/widgets/number-field';
import { selectField } from '../fields/widgets/select-field';
import { createSurfaceFillField, SURFACE_COLOR_FIELD_AI_SCHEMA } from '../fields/widgets/surface-color-field';
import { toggleField } from '../fields/widgets/toggle-field';
import { buildImgixDelivery, isImgixHostedSrc } from '../image-src';
import { interpolateFromPuck } from '../interpolation';
import { resolveImageSource } from '../store-images';
import type { ImageSource } from '../store-images';
import { imgixSizing } from './image-style';
import { isSafeSrc } from './image';

const HEADING_LEVEL_OPTIONS = [
  { label: 'H2', value: 'h2' },
  { label: 'H3', value: 'h3' },
  { label: 'H4', value: 'h4' },
] as const;

const IMAGE_ASPECT_RATIO_OPTIONS: ReadonlyArray<{ label: string; value: ImageAspectRatio }> = [
  { label: 'Original', value: 'original' },
  { label: 'Square (1:1)', value: 'square' },
  { label: 'Landscape (4:3)', value: 'landscape' },
  { label: 'Wide (16:9)', value: 'wide' },
  { label: 'Portrait (3:4)', value: 'portrait' },
];

const IMAGE_FIT_OPTIONS = [
  { label: 'Fill the frame', value: 'cover' },
  { label: 'Fit inside', value: 'contain' },
] as const;

const ACTION_STYLE_OPTIONS = [
  { label: 'Primary', value: 'primary' },
  { label: 'Secondary', value: 'secondary' },
  { label: 'Tertiary', value: 'tertiary' },
] as const;

export const TILE_BADGE_TONE_OPTIONS = [
  { label: 'Neutral', value: 'neutral' },
  { label: 'Brand', value: 'brand' },
] as const;

const ALIGNMENT_OPTIONS = [
  { label: 'Left', value: 'left' },
  { label: 'Center', value: 'center' },
] as const;

export const TILE_MAX_WIDTH_OPTIONS: ReadonlyArray<{ label: string; value: ContainerWidthOption }> = [
  { label: 'Narrow', value: 'narrow' },
  { label: 'Normal', value: 'normal' },
  { label: 'Wide', value: 'wide' },
  { label: 'Full', value: 'none' },
];

export const TILE_ALIGN_OPTIONS = [
  { label: 'Left', value: 'left' },
  { label: 'Center', value: 'center' },
  { label: 'Right', value: 'right' },
] as const;

const RADIUS_OPTIONS = [
  { label: 'Theme card radius', value: 'theme' },
  { label: 'None', value: 'none' },
] as const;

const PADDING_OPTIONS: ReadonlyArray<{ label: string; value: BlockSpacing }> = [
  { label: 'Extra small (8px)', value: 'xs' },
  { label: 'Small (16px)', value: 'sm' },
  { label: 'Medium (24px)', value: 'md' },
  { label: 'Large (40px)', value: 'lg' },
];

const ACTION_STYLES = {
  primary: {
    background: BRAND_COLOR,
    color: BRAND_CONTRAST_COLOR,
    border: 'none',
  },
  secondary: {
    background: 'transparent',
    color: BRAND_COLOR,
    border: `2px solid ${BRAND_COLOR}`,
  },
  tertiary: {
    background: 'transparent',
    color: BRAND_COLOR,
    border: 'none',
  },
} as const;

const TILE_BADGE_TONES = {
  neutral: {
    background:
      'var(--dcm-cms-tile-badge-neutral-bg, var(--dcm-cms-tile-badge-bg, var(--dcm-color-badge-neutral-bg, var(--dcm-color-surface-muted, #f5f6f7))))',
    color:
      'var(--dcm-cms-tile-badge-neutral-color, var(--dcm-cms-tile-badge-color, var(--dcm-color-badge-neutral-text, #485055)))',
  },
  brand: {
    background: BRAND_COLOR,
    color: BRAND_CONTRAST_COLOR,
  },
} as const;

const CONTENT_ALIGNS = { left: 'left', center: 'center' } as const;
const HEADING_LEVELS = { h2: 'h2', h3: 'h3', h4: 'h4' } as const;
const IMAGE_FITS = { cover: 'cover', contain: 'contain' } as const;
const TILE_RADII = {
  theme: 'var(--dcm-radius-cards, 12px)',
  none: '0',
} as const;
const PIXEL_LENGTH = /^(\d+)px$/;
const MAX_BORDER_WIDTH_PX = 20;

type TileHeadingLevel = keyof typeof HEADING_LEVELS;
type TileImageFit = keyof typeof IMAGE_FITS;
type TileActionStyle = keyof typeof ACTION_STYLES;
type TileBadgeTone = keyof typeof TILE_BADGE_TONES;
type TileContentAlign = keyof typeof CONTENT_ALIGNS;
export type TileAlign = typeof TILE_ALIGN_OPTIONS[number]['value'];
type TileRadius = keyof typeof TILE_RADII;

export type TileProps = {
  badge: string;
  heading: string;
  headingLevel: TileHeadingLevel;
  body: string;
  source: ImageSource;
  alt: AltTextValue;
  aspectRatio: ImageAspectRatio;
  objectFit: TileImageFit;
  focalPoint: ImageFocalPoint;
  actionLabel: string;
  actionDestination: string;
  actionOpenInNewTab: boolean;
  actionStyle: TileActionStyle;
  size: 'fit-content' | 'match-image';
  textPlacement: 'below' | 'overlay';
  badgeTone: TileBadgeTone;
  surfaceColor: string;
  textColor: string;
  contentAlign: TileContentAlign;
  borderColor: BorderColor;
  borderWidth: string;
  radius: TileRadius;
  contentPadding: BlockSpacing;
  maxWidth: ContainerWidthOption;
  align: TileAlign;
  overlayOpacity: number;
};

export const DEFAULT_TILE_PROPS: TileProps = {
  badge: '',
  heading: 'Your tile title',
  headingLevel: 'h3',
  body: 'Add a short description that supports your title.',
  source: { kind: 'url', src: '' },
  alt: { alt: '', decorative: false },
  aspectRatio: 'landscape',
  objectFit: 'cover',
  focalPoint: 'center',
  actionLabel: 'Shop now',
  actionDestination: '/shop',
  actionOpenInNewTab: false,
  actionStyle: 'primary',
  size: 'fit-content',
  textPlacement: 'below',
  badgeTone: 'neutral',
  surfaceColor: 'surface',
  textColor: INHERIT_COLOR,
  contentAlign: 'left',
  borderColor: { ...DEFAULT_BORDER.color },
  borderWidth: DEFAULT_BORDER.width,
  radius: 'theme',
  contentPadding: 'md',
  maxWidth: 'none',
  align: 'center',
  overlayOpacity: DEFAULT_BACKGROUND_IMAGE_PROPS.overlayOpacity,
};

const TileSurfaceFillField = createSurfaceFillField(DEFAULT_TILE_PROPS.surfaceColor);

type UnknownRecord = Record<string, unknown>;

function asRecord(value: unknown): UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as UnknownRecord) : {};
}

function asString(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback;
}

function normalizeBorder(borderColor: unknown, borderWidth: unknown): BorderValue {
  const candidateColor = asRecord(borderColor);
  const { color: defaultColor } = DEFAULT_BORDER;
  let color: BorderColor = defaultColor;

  if (candidateColor.style === 'none' || candidateColor.style === 'theme') {
    color = { style: candidateColor.style };
  } else if (
    candidateColor.style === 'custom' &&
    typeof candidateColor.hex === 'string' &&
    isHexColor(candidateColor.hex)
  ) {
    color = { style: 'custom', hex: candidateColor.hex };
  }

  const candidateWidth = typeof borderWidth === 'string' ? borderWidth : '';
  const widthMatch = PIXEL_LENGTH.exec(candidateWidth);
  const widthPx = widthMatch ? Number(widthMatch[1]) : NaN;

  return {
    color,
    width:
      Number.isInteger(widthPx) && widthPx >= 0 && widthPx <= MAX_BORDER_WIDTH_PX
        ? candidateWidth
        : DEFAULT_BORDER.width,
  };
}

function normalizeSurface(value: unknown): string {
  const surface = asString(value, DEFAULT_TILE_PROPS.surfaceColor);
  if (surface === 'none' || surface === 'surface' || surface === 'muted' || isHexColor(surface)) return surface;
  return DEFAULT_TILE_PROPS.surfaceColor;
}

function normalizeTileImageSource(value: unknown): ImageSource {
  const source = asRecord(value);
  if (source.kind === 'url' && typeof source.src === 'string') return { kind: 'url', src: source.src };
  if (source.kind === 'library' && typeof source.assetId === 'string' && typeof source.src === 'string') {
    return { kind: 'library', assetId: source.assetId, src: source.src };
  }
  return DEFAULT_TILE_PROPS.source;
}

export function resolveTileMaxWidth(value: unknown): string {
  if (typeof value === 'string' && Object.prototype.hasOwnProperty.call(CONTAINER_WIDTHS, value)) {
    return CONTAINER_WIDTHS[value as ContainerWidthOption];
  }
  return CONTAINER_WIDTHS.none;
}

export function resolveTileAlign(value: unknown): TileAlign {
  if (value === 'left' || value === 'right') return value;
  return 'center';
}

const TILE_ALIGN_MARGINS: Record<TileAlign, { marginLeft: string; marginRight: string }> = {
  left: { marginLeft: '0', marginRight: 'auto' },
  center: { marginLeft: 'auto', marginRight: 'auto' },
  right: { marginLeft: 'auto', marginRight: '0' },
};

const TileRoot = styled.div`
  background: var(--cms-tile-surface, var(--dcm-color-surface, #ffffff));
  border-radius: var(--cms-tile-radius, var(--dcm-radius-cards, 12px));
  box-sizing: border-box;
  color: var(--cms-tile-text-color, inherit);
  display: flex;
  flex-direction: column;
  height: 100%;
  margin-left: var(--cms-tile-margin-left, auto);
  margin-right: var(--cms-tile-margin-right, auto);
  max-width: var(--cms-tile-max-width, none);
  min-width: 0;
  overflow: hidden;
  text-align: var(--cms-tile-content-align, left);
  width: 100%;

  &[data-text-placement] {
    display: grid;
    isolation: isolate;
    position: relative;

    > img {
      grid-area: 1 / 1;
      height: 100%;
      min-height: 0;
    }

    > div {
      grid-area: 1 / 1;
      z-index: 1;
    }
  }

  &[data-size] {
    align-self: start;
    display: block;
    height: auto;
    position: relative;

    > img {
      height: auto;
    }

    /* Only the image contributes to the card's height; the overlay scrolls within it. */
    > span,
    > div {
      inset: 0;
      position: absolute;
    }

    > div {
      min-height: 0;
      overflow: auto;
      overflow-wrap: anywhere;
    }

    > div > a {
      max-width: 100%;
    }
  }
`;

const TileImage = styled.img`
  aspect-ratio: var(--cms-tile-image-aspect-ratio, 4 / 3);
  display: block;
  height: auto;
  object-fit: var(--cms-tile-image-fit, cover);
  object-position: var(--cms-tile-image-focal-point, center center);
  width: 100%;
`;

const TileContent = styled.div`
  box-sizing: border-box;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--dcm-cms-tile-content-gap, 16px);
  min-width: 0;
  padding: var(--cms-tile-content-padding, 24px);
`;

const TileBadge = styled.span`
  align-self: var(--cms-tile-inline-align, flex-start);
  background: var(--cms-tile-badge-bg, var(--dcm-cms-tile-badge-bg, var(--dcm-color-surface-muted, #f5f6f7)));
  border-radius: var(--dcm-cms-tile-badge-radius, 9999px);
  color: var(--cms-tile-badge-color, var(--dcm-cms-tile-badge-color, #485055));
  font-family: var(--dcm-font-secondary, inherit);
  font-size: var(--dcm-cms-tile-badge-font-size, 12px);
  font-weight: var(--dcm-cms-tile-badge-font-weight, 600);
  line-height: var(--dcm-cms-tile-badge-line-height, 1.3);
  padding: var(--dcm-cms-tile-badge-inset, 4px 8px);
`;

const TileHeading = styled.h3`
  color: inherit;
  font-family: var(--dcm-font-primary, inherit);
  font-size: var(--cms-tile-heading-size, ${HEADING_PRESETS.h3.fontSize});
  font-weight: var(--cms-tile-heading-weight, ${HEADING_PRESETS.h3.fontWeight});
  line-height: ${HEADING_LINE_HEIGHT};
  margin: 0;
  overflow-wrap: anywhere;
`;

const TileBody = styled.p`
  color: inherit;
  font-family: var(--dcm-font-secondary, inherit);
  font-size: var(--dcm-cms-tile-body-font-size, 16px);
  line-height: var(--dcm-line-height-normal, 1.43);
  margin: 0;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
`;

const TileAction = styled.a`
  align-self: var(--cms-tile-inline-align, flex-start);
  background: var(--cms-tile-action-background, ${ACTION_STYLES.primary.background});
  border: var(--cms-tile-action-border, none);
  border-radius: ${BUTTON_RADIUS};
  box-sizing: border-box;
  color: var(--cms-tile-action-color, ${ACTION_STYLES.primary.color});
  cursor: pointer;
  display: inline-block;
  font-family: var(--dcm-font-secondary, inherit);
  font-weight: ${BUTTON_FONT_WEIGHT};
  margin-top: auto;
  padding: ${BUTTON_INSET};
  text-align: center;
  text-decoration: none;

  &:focus-visible {
    outline: var(--dcm-cms-tile-focus-width, 3px) solid
      var(--dcm-cms-tile-focus-color, var(--dcm-color-primary, #2563eb));
    outline-offset: var(--dcm-cms-tile-focus-offset, 3px);
  }
`;

const TileMediaWarning = styled.p`
  background: var(--dcm-cms-tile-warning-bg, #fff8e6);
  color: var(--dcm-cms-tile-warning-color, #7a4300);
  font-family: var(--dcm-font-secondary, inherit);
  font-size: var(--dcm-cms-tile-warning-font-size, 12px);
  line-height: var(--dcm-cms-tile-warning-line-height, 1.4);
  margin: 0;
  padding: var(--dcm-cms-tile-warning-inset, 8px 12px);
  /* Stay above the full-card image overlay (z-index 0) so the warning is never dimmed. */
  position: relative;
  z-index: 1;
`;

const TILE_FIELD_GROUPS: readonly FieldGroupDefinition<keyof TileProps>[] = [
  { label: 'Content', fields: ['badge', 'body'] },
  { label: 'Image', fields: ['aspectRatio', 'objectFit', 'focalPoint'] },
  {
    label: 'Appearance',
    fields: [
      'textPlacement',
      'overlayOpacity',
      'surfaceColor',
      'textColor',
      'badgeTone',
      'borderColor',
      'borderWidth',
      'radius',
    ],
  },
  { label: 'Layout', fields: ['size', 'contentAlign', 'maxWidth', 'align'] },
  { label: 'Link', fields: ['actionLabel', 'actionDestination', 'actionOpenInNewTab', 'actionStyle'] },
  { label: 'Accessibility', fields: ['headingLevel', 'alt'] },
  { label: 'Spacing', fields: ['contentPadding'] },
];

/** A constrained, owned-content card. Parent layouts own allocation; Tile may cap and align its own card. */
export const tileConfig: ComponentConfig<TileProps> = {
  label: 'Tile',
  ai: {
    instructions:
      'A constrained content card with an optional badge and image, one plain-text heading and body, and exactly one CTA. Maximum width and alignment make standalone cards usable; Tile still fills the available allocation up to that cap. Overlay text placement puts the content over the image with a strong scrim and inverted text; without a usable image it stays Below. Tile has no child slot or grid span control; place independent Tiles in Grid Layout for responsive collections.',
  },
  fields: {
    source: {
      type: 'custom',
      label: 'Image source',
      render: AssetOrUrlImageSourceField,
      ai: { exclude: true },
    },
    alt: altTextField(
      'Alt text',
      'Describe an informative image for screen readers, or explicitly mark purely visual media decorative.'
    ),
    aspectRatio: selectField('Shape', IMAGE_ASPECT_RATIO_OPTIONS),
    objectFit: selectField(
      'Crop',
      IMAGE_FIT_OPTIONS,
      "'cover' fills and crops the selected shape; 'contain' keeps the whole image visible."
    ),
    focalPoint: focalPointField(
      'Focal point',
      'Which part of the image remains visible when a fixed shape uses cover cropping.',
      "Only affects fixed image shapes when Crop is 'Fill the frame'."
    ),
    size: {
      type: 'radio',
      label: 'Size',
      options: [
        { label: 'Fit content', value: 'fit-content' },
        { label: 'Match image', value: 'match-image' },
      ],
      ai: { schema: { type: 'string', enum: ['fit-content', 'match-image'] } },
    },
    textPlacement: selectField(
      'Text placement',
      [
        { label: 'Below', value: 'below' },
        { label: 'Overlay', value: 'overlay' },
      ],
      'Overlay places text over the image with a dark overlay and inverted text for contrast; overlayOpacity (0 to 100) sets how dark. Without a usable image, text stays below.'
    ),
    overlayOpacity: BACKGROUND_IMAGE_FIELDS.overlayOpacity,
    surfaceColor: {
      type: 'custom',
      label: 'Surface',
      render: TileSurfaceFillField,
      ai: { schema: SURFACE_COLOR_FIELD_AI_SCHEMA },
    },
    textColor: {
      type: 'custom',
      label: 'Text color',
      render: ContainerTextColorField,
      ai: { schema: (CONTAINER_TEXT_COLOR_AI_SCHEMA as unknown) as Record<string, unknown> },
    },
    badgeTone: {
      type: 'radio',
      label: 'Badge tone',
      options: [...TILE_BADGE_TONE_OPTIONS],
      ai: { schema: { type: 'string', enum: TILE_BADGE_TONE_OPTIONS.map(({ value }) => value) } },
    },
    contentAlign: {
      type: 'radio',
      label: 'Content alignment',
      options: [...ALIGNMENT_OPTIONS],
      ai: { schema: { type: 'string', enum: ALIGNMENT_OPTIONS.map(({ value }) => value) } },
    },
    borderColor: {
      type: 'custom',
      label: 'Border color',
      render: BorderColorField,
      ai: { schema: BORDER_COLOR_AI_SCHEMA },
    },
    borderWidth: {
      type: 'custom',
      label: 'Border width',
      render: NumberField('px', DEFAULT_BORDER.width),
      ai: { schema: PX_AI_SCHEMA },
    },
    radius: {
      type: 'radio',
      label: 'Corner radius',
      options: [...RADIUS_OPTIONS],
      ai: { schema: { type: 'string', enum: RADIUS_OPTIONS.map(({ value }) => value) } },
    },
    contentPadding: {
      type: 'select',
      label: 'Content padding',
      options: PADDING_OPTIONS,
      ai: { schema: { type: 'string', enum: PADDING_OPTIONS.map(({ value }) => value) } },
    },
    maxWidth: selectField(
      'Maximum width',
      TILE_MAX_WIDTH_OPTIONS,
      "Caps the card width while keeping it responsive. 'Narrow', 'Normal', and 'Wide' use the shared Web Studio container scale; 'Full' fills the available allocation."
    ),
    align: selectField('Alignment', TILE_ALIGN_OPTIONS),
    badge: { type: 'text', label: 'Badge', placeholder: 'Optional badge' },
    heading: { type: 'text', label: 'Heading' },
    headingLevel: {
      type: 'radio',
      label: 'Heading level',
      options: [...HEADING_LEVEL_OPTIONS],
      ai: { schema: { type: 'string', enum: HEADING_LEVEL_OPTIONS.map(({ value }) => value) } },
    },
    body: { type: 'textarea', label: 'Body' },
    actionLabel: { type: 'text', label: 'Call to action label' },
    actionDestination: destinationField('Call to action destination', {
      instructions: 'Internal storefront page or external URL for the Tile CTA.',
    }),
    actionOpenInNewTab: toggleField('Open call to action in new tab'),
    actionStyle: {
      type: 'radio',
      label: 'Call to action style',
      options: [...ACTION_STYLE_OPTIONS],
      ai: { schema: { type: 'string', enum: ACTION_STYLE_OPTIONS.map(({ value }) => value) } },
    },
  },
  renderFields: ({ fields }) => (
    <>
      {fields.heading}
      {fields.source}
      {renderFieldGroups(fields, TILE_FIELD_GROUPS)}
    </>
  ),
  resolveFields: ({ props }, { fields }) => {
    const matchImage = props.size === 'match-image';
    const effectiveAspectRatio =
      IMAGE_ASPECT_RATIO_OPTIONS.find(({ value }) => value === props.aspectRatio)?.value ??
      DEFAULT_TILE_PROPS.aspectRatio;
    const effectiveObjectFit = lookup(
      IMAGE_FITS,
      asString(props.objectFit, DEFAULT_TILE_PROPS.objectFit),
      IMAGE_FITS[DEFAULT_TILE_PROPS.objectFit]
    );
    const hasCroppingFrame = effectiveAspectRatio !== 'original';
    const hidden = new Set<string>();
    if (matchImage) {
      [
        'aspectRatio',
        'objectFit',
        'focalPoint',
        'textPlacement',
        'surfaceColor',
        'textColor',
        'borderColor',
        'borderWidth',
        'radius',
      ].forEach((key) => hidden.add(key));
    } else {
      if (!hasCroppingFrame) hidden.add('objectFit');
      if (!hasCroppingFrame || effectiveObjectFit !== 'cover') hidden.add('focalPoint');
      if (props.textPlacement === 'overlay') {
        hidden.add('surfaceColor');
        hidden.add('textColor');
      }
    }
    // The overlay only exists when the image sits behind the text, so offer its opacity only then.
    const resolvedSource = resolveImageSource(normalizeTileImageSource(props.source), undefined);
    const hasUsableImage = resolvedSource.src.trim() !== '' && isSafeSrc(resolvedSource.src);
    if (!hasUsableImage || !(matchImage || props.textPlacement === 'overlay')) hidden.add('overlayOpacity');
    if (typeof props.badge !== 'string' || props.badge.trim() === '') hidden.add('badgeTone');
    if (props.maxWidth !== 'narrow' && props.maxWidth !== 'normal' && props.maxWidth !== 'wide') hidden.add('align');
    // Only the inspector changes; hidden authored values stay in the saved props.
    return Object.fromEntries(Object.entries(fields).filter(([key]) => !hidden.has(key))) as Fields<TileProps>;
  },
  defaultProps: DEFAULT_TILE_PROPS,
  render: function TileRender(rawProps) {
    const [failedImageSource, setFailedImageSource] = React.useState<string | null>(null);
    const imageRef = React.useRef<HTMLImageElement>(null);
    const headingId = React.useId();

    const badge = interpolateFromPuck(asString(rawProps.badge, DEFAULT_TILE_PROPS.badge), rawProps.puck);
    const heading = interpolateFromPuck(asString(rawProps.heading, DEFAULT_TILE_PROPS.heading), rawProps.puck);
    const headingLevel = lookup(
      HEADING_LEVELS,
      asString(rawProps.headingLevel, DEFAULT_TILE_PROPS.headingLevel),
      HEADING_LEVELS[DEFAULT_TILE_PROPS.headingLevel]
    );
    const body = interpolateFromPuck(asString(rawProps.body, DEFAULT_TILE_PROPS.body), rawProps.puck);

    const source = resolveImageSource(normalizeTileImageSource(rawProps.source), undefined);
    const resolvedAlt = resolveAltText(rawProps.alt);
    const alt = resolvedAlt.decorative ? '' : resolvedAlt.alt.trim();
    const aspectRatioKey = asString(rawProps.aspectRatio, DEFAULT_TILE_PROPS.aspectRatio);
    const aspectRatio = lookup(
      IMAGE_ASPECT_RATIOS,
      aspectRatioKey,
      IMAGE_ASPECT_RATIOS[DEFAULT_TILE_PROPS.aspectRatio]
    );
    const objectFit = lookup(
      IMAGE_FITS,
      asString(rawProps.objectFit, DEFAULT_TILE_PROPS.objectFit),
      IMAGE_FITS[DEFAULT_TILE_PROPS.objectFit]
    );
    const focalPoint = lookup(
      IMAGE_FOCAL_POINTS,
      asString(rawProps.focalPoint, DEFAULT_TILE_PROPS.focalPoint),
      IMAGE_FOCAL_POINTS[DEFAULT_TILE_PROPS.focalPoint]
    );

    const actionLabel = interpolateFromPuck(
      asString(rawProps.actionLabel, DEFAULT_TILE_PROPS.actionLabel),
      rawProps.puck
    ).trim();
    const rawDestination = interpolateFromPuck(
      asString(rawProps.actionDestination, DEFAULT_TILE_PROPS.actionDestination),
      rawProps.puck
    ).trim();
    const sanitizedDestination = rawDestination === '' ? '' : sanitizeUrl(rawDestination);
    const safeDestination = sanitizedDestination === 'about:blank' ? '' : sanitizedDestination;
    const openInNewTab = (rawProps.actionOpenInNewTab as unknown) === true;
    const actionStyle = lookup(
      ACTION_STYLES,
      asString(rawProps.actionStyle, DEFAULT_TILE_PROPS.actionStyle),
      ACTION_STYLES[DEFAULT_TILE_PROPS.actionStyle]
    );

    const badgeTone = lookup(
      TILE_BADGE_TONES,
      asString(rawProps.badgeTone, DEFAULT_TILE_PROPS.badgeTone),
      TILE_BADGE_TONES[DEFAULT_TILE_PROPS.badgeTone]
    );
    const surfaceColor = normalizeSurface(rawProps.surfaceColor);
    const textColor = asString(rawProps.textColor, DEFAULT_TILE_PROPS.textColor);
    const contentAlign = lookup(
      CONTENT_ALIGNS,
      asString(rawProps.contentAlign, DEFAULT_TILE_PROPS.contentAlign),
      CONTENT_ALIGNS[DEFAULT_TILE_PROPS.contentAlign]
    );
    const border = normalizeBorder(rawProps.borderColor, rawProps.borderWidth);
    const radius = lookup(
      TILE_RADII,
      asString(rawProps.radius, DEFAULT_TILE_PROPS.radius),
      TILE_RADII[DEFAULT_TILE_PROPS.radius]
    );
    const padding = lookup(
      BLOCK_SPACING,
      asString(rawProps.contentPadding, DEFAULT_TILE_PROPS.contentPadding),
      BLOCK_SPACING[DEFAULT_TILE_PROPS.contentPadding]
    );
    const maxWidth = resolveTileMaxWidth(rawProps.maxWidth);
    const align = resolveTileAlign(rawProps.align);

    // A selected informative image never reaches production with an empty alt. Puck 0.23 has no
    // publish-validation hook for component fields, so Tile uses the strongest supported boundary:
    // keep the image visible with an explicit editor warning, then fail closed to no media in Render.
    const hasSafeSource = source.src.trim() !== '' && isSafeSrc(source.src);
    const missingInformativeAlt = hasSafeSource && !resolvedAlt.decorative && alt === '';
    // `puck` is present in Puck/Render but direct component tests may omit it.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const isEditing = Boolean(rawProps.puck?.isEditing);
    const matchImageRequested = rawProps.size === 'match-image';
    React.useEffect(() => {
      // An image can fail before hydration attaches React's error handler.
      const image = imageRef.current;
      setFailedImageSource(matchImageRequested && image?.complete && image.naturalWidth === 0 ? source.src : null);
    }, [matchImageRequested, source.src]);
    const shouldRenderImage =
      hasSafeSource &&
      (!missingInformativeAlt || isEditing) &&
      !(matchImageRequested && failedImageSource === source.src);
    const isMatchImage = matchImageRequested && shouldRenderImage && !missingInformativeAlt;
    const isOverlay =
      (isMatchImage || (!matchImageRequested && rawProps.textPlacement === 'overlay')) && shouldRenderImage;
    const imgixDelivery =
      shouldRenderImage && isImgixHostedSrc(source.src)
        ? buildImgixDelivery(source.src, imgixSizing('full'), 'auto, 100vw')
        : undefined;

    const preset = HEADING_PRESETS[headingLevel as HeadingPreset];
    const belowTextColor =
      textColor === INHERIT_COLOR ? inheritedTextColorFor(surfaceColor) : 'var(--dcm-color-text-primary, #485055)';
    const rootStyle = {
      ...containerColorStyle(textColor),
      ...(isMatchImage ? {} : borderStyle(border)),
      '--cms-tile-surface': resolveSurfaceColor(surfaceColor),
      // Containers scope text through --dcm-color-text-primary instead of setting CSS color.
      // Reading that variable keeps Tile's "Inherit" honest inside Grid/Section/Flex while the Tile
      // is transparent or custom-colored. On its own theme surface, Inherit pairs with that surface
      // so an Inverted container cannot hand white text to a white card. An explicit Tile text
      // color still wins: containerColorStyle above re-points the token itself.
      '--cms-tile-text-color': isOverlay ? TEXT_COLOR_ROLES.inverted : belowTextColor,
      '--cms-tile-content-align': contentAlign,
      '--cms-tile-inline-align': contentAlign === 'center' ? 'center' : 'flex-start',
      '--cms-tile-radius': isMatchImage ? '0' : radius,
      '--cms-tile-content-padding': padding,
      '--cms-tile-max-width': maxWidth,
      '--cms-tile-margin-left': TILE_ALIGN_MARGINS[align].marginLeft,
      '--cms-tile-margin-right': TILE_ALIGN_MARGINS[align].marginRight,
    } as React.CSSProperties;

    const imageStyle = {
      '--cms-tile-image-aspect-ratio': isMatchImage ? IMAGE_ASPECT_RATIOS.original : aspectRatio,
      '--cms-tile-image-fit': objectFit,
      '--cms-tile-image-focal-point':
        objectFit === 'cover' && aspectRatioKey !== 'original' ? focalPoint : 'center center',
    } as React.CSSProperties;

    const badgeStyle = {
      '--cms-tile-badge-bg': badgeTone.background,
      '--cms-tile-badge-color': badgeTone.color,
    } as React.CSSProperties;

    const actionStyleProperties = {
      '--cms-tile-action-background': actionStyle.background,
      '--cms-tile-action-color':
        isOverlay && (rawProps.actionStyle === 'secondary' || rawProps.actionStyle === 'tertiary')
          ? TEXT_COLOR_ROLES.inverted
          : actionStyle.color,
      '--cms-tile-action-border':
        isOverlay && rawProps.actionStyle === 'secondary'
          ? `2px solid ${TEXT_COLOR_ROLES.inverted}`
          : actionStyle.border,
    } as React.CSSProperties;

    return (
      <TileRoot
        data-testid='cms-tile'
        data-text-placement={isOverlay ? 'overlay' : undefined}
        data-size={isMatchImage ? 'match-image' : undefined}
        style={rootStyle}
      >
        {shouldRenderImage ? (
          <TileImage
            ref={imageRef}
            src={imgixDelivery?.src ?? source.src}
            {...(imgixDelivery
              ? {
                  srcSet: imgixDelivery.srcSet,
                  ...(imgixDelivery.sizes !== undefined ? { sizes: imgixDelivery.sizes } : {}),
                }
              : {})}
            alt={alt}
            onError={matchImageRequested ? () => setFailedImageSource(source.src) : undefined}
            loading='lazy'
            style={imageStyle}
          />
        ) : null}
        {missingInformativeAlt && isEditing ? (
          <TileMediaWarning data-testid='cms-tile-missing-alt-warning' role='status'>
            This image will not publish until you add alt text or mark it decorative.
          </TileMediaWarning>
        ) : null}
        {isOverlay ? (
          <BackgroundOverlay
            as='span'
            data-testid='cms-tile-overlay'
            aria-hidden='true'
            style={backgroundOverlayStyle(rawProps.overlayOpacity)}
          />
        ) : null}
        <TileContent
          role={isMatchImage ? 'region' : undefined}
          aria-label={isMatchImage && heading.trim() === '' ? 'Tile content' : undefined}
          aria-labelledby={isMatchImage && heading.trim() !== '' ? headingId : undefined}
          tabIndex={isMatchImage ? 0 : undefined}
        >
          {badge.trim() !== '' ? <TileBadge style={badgeStyle}>{badge}</TileBadge> : null}
          {heading.trim() !== '' ? (
            <TileHeading
              id={isMatchImage ? headingId : undefined}
              as={headingLevel}
              style={
                {
                  '--cms-tile-heading-size': preset.fontSize,
                  '--cms-tile-heading-weight': preset.fontWeight,
                } as React.CSSProperties
              }
            >
              {heading}
            </TileHeading>
          ) : null}
          {body.trim() !== '' ? <TileBody>{body}</TileBody> : null}
          {actionLabel !== '' && safeDestination !== '' ? (
            <TileAction
              href={safeDestination}
              target={openInNewTab ? '_blank' : undefined}
              rel={openInNewTab ? 'noopener noreferrer' : undefined}
              style={actionStyleProperties}
            >
              {actionLabel}
            </TileAction>
          ) : null}
        </TileContent>
      </TileRoot>
    );
  },
};
